import type { Dictionary } from './ru';

/** Английский словарь. Структура — ровно как у русского, см. `ru.ts`. */
export const en: Dictionary = {
  app: {
    name: 'Faceta',
  },
  nav: {
    path: 'Path',
    sections: 'Sections',
    collapse: 'Collapse sidebar',
    expand: 'Expand sidebar',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    sites: 'Sites',
    tenants: 'Tenants',
    home: 'Home',
    content: 'Content',
    media: 'Media',
    constants: 'Constants',
    pages: 'Pages',
    models: 'Models',
    forms: 'Forms',
    users: 'Users',
    automations: 'Automations',
    shop: 'Shop',
    components: 'Components',
    favorites: 'Favorites',
    navigation: 'Navigation',
    otherSites: 'Other sites',
    model: 'Models',
    transfer: 'Transfer',
    showMore: (n: number) => `${String(n)} more`,
    showLess: 'Show less',
    settings: 'Settings',
    team: 'Team',
    activity: 'Activity',
    search: 'Search',
    searchHint: 'Search and jump to',
    more: 'More',
    allSites: 'All sites',
    record: 'Record',
  },
  env: {
    devOnlyNote:
      'You are limited to Development. This is shared by both environments — a change would affect Production, so it is view-only here.',
    switcher: 'Environment',
    production: 'Production',
    development: 'Development',
    stripe:
      'Development — test environment: changes show on the site’s test stands, not on the live site.',
    missingDevelopment: 'This site has no Development environment yet',
    tabPrefix: '[DEV]',
  },
  userMenu: {
    theme: 'Theme',
    themeSystem: 'System',
    themeDark: 'Dark',
    themeLight: 'Light',
    language: 'Language',
    signOut: 'Sign out',
    signOutFailed: 'Could not sign out',
  },
  impersonation: {
    text: 'You are acting as',
    back: 'Back to my account',
    backFailed: 'Could not return to your account',
  },
  siteHome: {
    collection: 'Collection',
    single: 'Single',
    mediaHint: 'Images and files of the site',
    emptyTitle: 'No collections yet',
    emptyShaper:
      'Describe the first collection in “Models” — which fields its records have. It will then appear in the menu on the left.',
    emptyEditor:
      'Collections are set up by the site admin or developer — once they exist, they will be here and in the menu on the left.',
    openModel: 'Open “Models”',
  },
  sites: {
    title: 'Sites',
    subtitle:
      'Every site has two environments — Production and Development — each with its own data.',
    create: 'Create site',
    createFirst: 'Create the first site',
    search: 'Search by name or short name',
    allTenants: 'All tenants',
    tenant: 'Tenant',
    trash: 'Trash',
    noAddress: 'No address',
    noAddressHint: 'The site is not connected yet — set its address in site settings',
    settings: 'Settings',
    actions: (name: string) => `Actions for “${name}”`,
    open: 'Open',
    remove: 'Delete',
    restore: 'Restore',
    restored: (name: string) => `Site “${name}” restored`,
    restoreFailed: 'Could not restore',
    removed: (name: string) => `Site “${name}” moved to trash`,
    undo: 'Undo',
    undoHint: 'The site can be restored from the trash',
    removeTitle: (name: string) => `Delete “${name}”?`,
    removePlain: 'The site goes to the trash with its environments. It can be restored from there.',
    loadFailed: 'Could not load sites',
    loadFailedHint: 'The server did not respond. Nothing is lost — try again.',
    retry: 'Retry',
    emptyTitle: 'No sites yet',
    emptyText:
      'A site is separate content with its own address. Production and Development environments are created with it.',
    trashEmpty: 'The trash is empty',
    notFoundTitle: 'Nothing found',
    notFoundText: 'No site matches the search and the selected tenant.',
    reset: 'Reset',
    more: 'Show more',
    deletedAt: 'In trash',
    unknownError: 'Try again.',
  },
  settings: {
    title: 'Site settings',
    general: 'Site',
    name: 'Name',
    slug: 'Short name',
    slugHint:
      'Latin letters, digits and hyphens. It names the site in API addresses — old requests stop working after a change.',
    save: 'Save',
    saved: 'Saved',
    failed: 'Could not save',
    addresses: 'Addresses',
    addressesText:
      'Where the site lives in each environment. Faceta uses this address for preview, for links in emails and to let the browser call the API.',
    url: 'Site address',
    urlHint: 'With http:// or https://. Empty — the site is not connected yet.',
    extra: 'Other allowed addresses',
    extraHint:
      'One per line, without a path: https://www.shop.ru. Rarely needed — for example, when the site opens both with and without www.',
    more: 'Advanced',
    saveAddress: 'Save address',
    languages: 'Languages',
    languagesText:
      'Which languages the site content is in. The first is the main one: its values live in the record fields; the others are translations of fields marked “Translatable”. A missing translation falls back to the main language.',
    main: 'main',
    makeMain: 'Make main',
    removeLanguage: (code: string) => `Remove ${code}`,
    addLanguage: 'Add language',
    languageCode: 'Code: ru, en, kk, pt-BR',
    mainChanged:
      'Changing the main language does not move values: fields keep their current text. Do it before filling the site.',
    noAccess: 'Site settings are changed by the site admin or developer.',
  },
  constants: {
    title: 'Constants',
    subtitle:
      'Phone, email, social links — values the site reads by key. No drafts: a saved value is live at once.',
    add: 'Add constant',
    empty: 'No constants yet',
    emptyHint:
      'Add a phone, email or social link — the site gets them all in one /v1/constants call.',
    emptyReader: 'No constants yet. A developer or site admin can add them.',
    general: 'General',
    edit: 'Edit',
    delete: 'Delete',
    actions: 'Constant actions',
    copyKey: 'Copy key',
    saving: 'Saving…',
    saved: 'Saved',
    failed: 'Not saved',
    stale: 'Someone changed this constant — showing the new value',
    createTitle: 'New constant',
    editTitle: 'Constant',
    name: 'Name',
    namePlaceholder: 'Phone',
    key: 'Key',
    keyHint: 'The site reads the value by it: constants.phone. Latin letters, digits, underscores.',
    keyChanged: 'The site will stop finding the value by the old key — update the site code too.',
    type: 'Type',
    widget: 'Editor',
    section: 'Section',
    sectionHint: 'Group on this screen: “Contacts”, “Socials”. Empty — “General”.',
    hint: 'Hint',
    hintHint: 'What to enter — shown to whoever fills it in.',
    save: 'Save',
    create: 'Create',
    cancel: 'Cancel',
    created: 'Constant created',
    deleteTitle: (name: string) => `Delete “${name}”?`,
    deleteText: (key: string) =>
      `The site will stop receiving the value for ${key}. It can only be recreated.`,
    deleted: 'Constant deleted',
    noAccess: 'No access to this site’s constants',
    moveUp: 'Move up',
    moveDown: 'Move down',
  },
  team: {
    title: 'Team',
    subtitle:
      'Who works on the site. A role is what a person can do; the scope is the whole tenant or only this site. An invitation is a link you pass to the person yourself.',
    roles: {
      admin: 'Admin',
      developer: 'Developer',
      editor: 'Editor',
      author: 'Author',
      viewer: 'Viewer',
      support: 'Support',
    },
    roleHints: {
      admin: 'Everything, including people and settings',
      developer: 'Models, settings and content, no people',
      editor: 'Any records and publishing',
      author: 'Drafts, no publishing',
      viewer: 'View only',
      support: 'Visitor chats (coming later)',
    },
    email: 'Email',
    role: 'Role',
    scope: 'Scope',
    wholeTenant: 'Whole tenant',
    thisSite: 'This site only',
    site: 'Site',
    chooseSite: 'Choose a site',
    devOnly: 'Development only',
    devOnlyHint: 'Production is not visible to this person at all',
    devOnlyBadge: 'dev only',
    invite: 'Invite',
    invitations: 'Invitations',
    people: 'People',
    nobody: 'Nobody yet — invite the first person.',
    loading: 'Loading…',
    actions: 'Actions',
    changeRole: 'Change role',
    setDevOnly: 'Limit to Development',
    unsetDevOnly: 'Remove the limit',
    remove: 'Remove role',
    revoke: (email: string) => `Revoke the invitation for ${email}`,
    enterAs: 'Act as this person',
    roleChanged: 'Role changed',
    removed: 'Role removed',
    revoked: 'Invitation revoked',
    failed: 'Could not complete the action',
    chooseSiteError: 'Choose a site for a site role',
    adminNoDevOnly: 'An admin cannot be limited to Development',
    linkNote:
      'Pass this link to the person. It will not be shown again — only its fingerprint is stored.',
    copy: 'Copy',
    copied: 'Copied',
    copyFailed: 'Could not copy',
    copyFailedHint: 'Select the link and copy it manually.',
    statuses: { active: 'active', invited: 'invited', blocked: 'blocked' },
    impersonated: (email: string) => `You are acting as ${email}`,
    impersonatedHint: 'Return to your account with the bar at the top.',
    impersonateFailed: 'Could not act as this person',
    tenantTitle: (name: string) => `People of “${name}”`,
    noAccess: 'The site team is managed by its admin.',
  },
  live: {
    offline:
      'No connection to the server — other people’s changes are not visible yet. Reconnecting…',
    viewing: (name: string) => `${name} is viewing`,
    editingField: (name: string, field: string) => `${name} is editing “${field}”`,
    support: 'support',
    savedByOther: 'This record was saved elsewhere',
    savedByOtherHint: 'You have unsaved changes, so the form did not update itself.',
    takeTheirs: 'Take their version',
  },
  activity: {
    title: 'Activity',
    siteSubtitle:
      'Who changed what and when on the site: records, schema, media, addresses and people.',
    platformSubtitle: 'All events of all tenants, including acting as another person.',
    empty: 'Nothing has happened yet.',
    emptyHint: 'As soon as someone changes something, it will appear here.',
    more: 'Show more',
    failed: 'Could not load the activity',
    today: 'Today',
    yesterday: 'Yesterday',
    deletedUser: 'Deleted account',
    asSupport: (email: string) => `acting as — actually ${email}`,
    actions: {
      'record.create': 'created record',
      'record.save': 'saved draft',
      'record.publish': 'published record',
      'record.unpublish': 'unpublished record',
      'record.delete': 'deleted record',
      'record.restore': 'restored record',
      'record.revert': 'reverted record to an earlier version',
      'record.discard': 'discarded unpublished changes',
      'record.transfer': 'transferred record from Development',
      'type.create': 'created type',
      'type.update': 'changed type',
      'type.reorder': 'reordered types in the menu',
      'type.delete': 'deleted type',
      'type.restore': 'restored type',
      'field.create': 'added field',
      'field.update': 'changed field',
      'field.hide': 'hid field',
      'field.restore': 'restored field',
      'field.reorder': 'reordered fields of type',
      'field.change_type': 'changed field kind',
      'schema.transfer': 'transferred schema',
      'key.rotate': 'rotated API key',
      'schema.rollback': 'rolled back schema transfer',
      'environment.refresh': 'refreshed Development from Production',
      'media.upload': 'uploaded file',
      'media.move': 'moved files to a folder',
      'media.purge': 'permanently deleted from trash',
      'media.replace': 'replaced file',
      'folder.create': 'created folder',
      'folder.update': 'changed folder',
      'folder.delete': 'moved folder to trash',
      'folder.restore': 'restored folder',
      'constant.create': 'created constant',
      'constant.update': 'changed constant',
      'constant.delete': 'deleted constant',
      'constant.reorder': 'reordered constants',
      'menu.create': 'created menu',
      'menu.update': 'changed menu',
      'menu.delete': 'deleted menu',
      'record.reorder': 'reordered records',
      'record.stage': 'changed record status',
      'media.update': 'changed file',
      'media.delete': 'deleted file',
      'media.restore': 'restored file',
      'site.create': 'created site',
      'site.update': 'renamed site',
      'site.delete': 'deleted site',
      'site.restore': 'restored site',
      'site.address': 'changed environment address',
      'tenant.create': 'created tenant',
      'tenant.update': 'changed tenant',
      'tenant.delete': 'deleted tenant',
      'tenant.restore': 'restored tenant',
      'member.invite': 'invited',
      'member.revoke': 'revoked the invitation for',
      'member.role': 'changed the role of',
      'member.remove': 'removed the role of',
      'member.join': 'accepted the invitation',
      'impersonation.start': 'started acting as',
      'impersonation.stop': 'stopped acting as',
    },
  },
  login: {
    title: 'Sign in to Faceta',
    subtitle: 'Administrator email and password.',
    email: 'Email',
    password: 'Password',
    submit: 'Sign in',
    already: 'You are already signed in as',
    toSites: 'To sites',
    networkError: 'Could not reach the server. Try again.',
    failed: 'Could not sign in. Try again.',
    switchTheme: 'Switch theme',
    switchLanguage: 'Переключить на русский',
  },
  invite: {
    checking: 'Checking the invitation…',
    brokenTitle: 'This link does not work',
    brokenText:
      'The invitation has expired, was already used or was revoked. Ask whoever invited you for a new one.',
    title: 'Invitation to Faceta',
    into: 'You are invited to',
    asRole: {
      tenant_admin: 'as a tenant admin',
      site_admin: 'as a site admin',
      site_editor: 'as a site editor',
    },
    onSite: 'on the site',
    address: 'Email',
    name: 'Your name',
    nameHint: 'Colleagues will see this name in the list of people.',
    namePlaceholder: 'Jane Smith',
    newPassword: 'Choose a password',
    password: 'Password',
    existingHint: 'You already have an account — sign in with your current password.',
    optional: 'Can be left empty',
    accept: 'Accept invitation',
    accepted: 'Invitation accepted',
    acceptedHint: 'You can sign in now.',
    failed: 'Could not accept the invitation.',
  },
  notFound: {
    title: 'This page does not exist',
    text: 'The address may have a typo, or what it pointed to is gone.',
    back: 'To sites',
  },
  date: {
    today: 'Today',
    tomorrow: 'Tomorrow',
    nextWeek: 'In a week',
    clear: 'Clear date',
    prevMonth: 'Previous month',
    nextMonth: 'Next month',
  },
  time: {
    justNow: 'just now',
    minutesAgo: (n: number) => `${String(n)} min ago`,
    hoursAgo: (n: number) => `${String(n)} h ago`,
    yesterday: 'yesterday',
  },
};
